/**
 * Create a company list on a search: empty ("New list") or from a CSV
 * ("Import CSV"). Either way the recruiter lands on the new list.
 *
 * The CSV path is the same upload Chopbook uses (presigned PUT → complete →
 * pitchbook_worker), with `source=csv_upload` and the name typed here. It
 * reads PitchBook's export headers, which is also what "Export CSV" writes, so
 * a list edited in Claude or Gemini comes back unchanged (Jared's short-term
 * path). Native `<dialog>` + `showModal()`, like `PitchBookUploadModal`: no
 * toasts or nested modals open from inside it.
 */
import { Button, ProgressBar, Spinner } from "@nu-assist/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, FileUp, X } from "lucide-react";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { CompanyListsClient } from "../../clients/companyLists.client";
import { usePitchBookUpload } from "../pitchbook/usePitchBookUpload";
import { companyListPath } from "./paths";
import { COMPANY_LISTS_KEY } from "./queries";

export type CompanyListDialogMode = "new" | "import";

interface CompanyListDialogProps {
  mode: CompanyListDialogMode;
  client: CompanyListsClient;
  searchId: string;
  onClose: () => void;
}

const MAX_NAME = 120;

function stem(filename: string): string {
  return filename.replace(/\.csv$/i, "");
}

export function CompanyListDialog({ mode, client, searchId, onClose }: CompanyListDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const nameId = useId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const upload = usePitchBookUpload();

  const create = useMutation({
    mutationFn: (listName: string) => client.create(searchId, listName),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: COMPANY_LISTS_KEY });
      onClose();
      navigate(companyListPath(created.list_id));
    },
  });

  const uploading = upload.state !== "idle" && upload.state !== "ready" && upload.state !== "error";
  const busy = create.isPending || uploading;

  const busyRef = useRef(busy);
  busyRef.current = busy;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Open as a modal once; Escape goes through our close so a running upload
  // is not abandoned by a stray key.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    // jsdom has no showModal(); the attribute is the same dialog, non-modal.
    if (!el.open) {
      if (typeof el.showModal === "function") el.showModal();
      else el.setAttribute("open", "");
    }
    const onCancel = (e: Event) => {
      e.preventDefault();
      if (!busyRef.current) onCloseRef.current();
    };
    el.addEventListener("cancel", onCancel);
    return () => el.removeEventListener("cancel", onCancel);
  }, []);

  // The upload finished: open the list it created.
  useEffect(() => {
    if (upload.state === "ready" && upload.listId) {
      onClose();
      navigate(companyListPath(upload.listId));
    }
  }, [upload.state, upload.listId, onClose, navigate]);

  const trimmed = name.replace(/\s+/g, " ").trim();
  const canSubmit =
    !busy && trimmed.length <= MAX_NAME && (mode === "new" ? trimmed.length > 0 : file !== null);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    if (mode === "new") {
      create.mutate(trimmed);
    } else if (file) {
      void upload.startUpload(file, searchId, {
        name: trimmed || stem(file.name),
        source: "csv_upload",
      });
    }
  }

  const error =
    create.error instanceof Error
      ? create.error.message
      : upload.state === "error"
        ? upload.error
        : null;
  const title = mode === "new" ? "New company list" : "Import a company list";

  return (
    <dialog
      ref={dialogRef}
      aria-label={title}
      data-testid="company-list-dialog"
      className="fixed inset-0 z-50 m-0 flex w-full max-w-none items-center justify-center border-none bg-transparent p-4 backdrop:bg-black/50"
    >
      <form
        onSubmit={submit}
        className="flex w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-h3 font-semibold text-text">{title}</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
            className="rounded-md p-1 text-text-muted hover:bg-surface-raised disabled:opacity-40"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          {mode === "import" && (
            <p className="text-body text-text-secondary">
              A CSV with PitchBook’s column names — a PitchBook export, or a list exported from here
              and edited elsewhere. Companies already known are matched, not duplicated.
            </p>
          )}
          <label htmlFor={nameId} className="flex flex-col gap-1">
            <span className="text-label font-medium text-text">
              List name{mode === "import" ? " (optional)" : ""}
            </span>
            <input
              id={nameId}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              maxLength={MAX_NAME * 2}
              placeholder={
                mode === "import" ? "Defaults to the file name" : "e.g. Robotics — Europe"
              }
              data-testid="company-list-name"
              className="rounded-md border border-border bg-bg px-3 py-2 text-body text-text focus:border-accent focus:outline-none"
            />
            {trimmed.length > MAX_NAME && (
              <span className="text-caption text-error">At most {MAX_NAME} characters.</span>
            )}
          </label>

          {mode === "import" && (
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                aria-label="CSV file"
                data-testid="company-list-file"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
              >
                <FileUp className="h-4 w-4" aria-hidden="true" />
                {file ? file.name : "Choose a CSV file"}
              </Button>
              {upload.state === "uploading" && (
                <ProgressBar value={upload.progress} max={100} label="Uploading" />
              )}
              {(upload.state === "finalizing" || upload.state === "polling") && (
                <div className="flex flex-col gap-1">
                  <ProgressBar indeterminate live label="Importing companies" />
                  <span className="text-label text-text-secondary">Importing companies…</span>
                </div>
              )}
            </div>
          )}

          {error && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-md border border-error-strong bg-error-muted px-3 py-2 text-label text-error"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit} data-testid="company-list-submit">
            {busy && <Spinner size="sm" label="Working…" />}
            {mode === "new" ? "Create list" : "Import"}
          </Button>
        </div>
      </form>
    </dialog>
  );
}
